import { collection, doc, addDoc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { JobListing, Provider, GovProgram, NgoProgram } from '@/lib/accesshub'

export type AdminJobInput = Omit<JobListing, 'id' | 'createdAt' | 'updatedAt'> & { source?: string }
export type AdminProviderInput = Omit<Provider, 'id' | 'createdAt' | 'updatedAt'>
export type AdminGovProgramInput = Omit<GovProgram, 'id' | 'createdAt' | 'updatedAt'>
export type AdminNgoProgramInput = Omit<NgoProgram, 'id' | 'createdAt' | 'updatedAt'>

/** Logs and rethrows so callers keep their own error handling (R7/R11). */
async function logged<T>(context: string, op: () => Promise<T>): Promise<T> {
  try {
    return await op()
  } catch (err: unknown) {
    console.warn(`[adminResources.${context}] write failed:`, err)
    throw err
  }
}

export async function adminGetJobs(): Promise<(JobListing & { source?: string })[]> {
  const snap = await getDocs(collection(db, 'jobs'))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as JobListing & { source?: string }))
    .sort((a, b) => b.createdAt - a.createdAt)
}

export async function adminCreateJob(data: AdminJobInput): Promise<string> {
  return logged('adminCreateJob', async () => {
    const ref = await addDoc(collection(db, 'jobs'), { ...data, createdAt: Date.now(), updatedAt: Date.now() })
    return ref.id
  })
}

export async function adminUpdateJob(id: string, data: Partial<AdminJobInput>): Promise<void> {
  await logged('adminUpdateJob', () => updateDoc(doc(db, 'jobs', id), { ...data, updatedAt: Date.now() }))
}

export async function adminDeleteJob(id: string): Promise<void> {
  await logged('adminDeleteJob', () => deleteDoc(doc(db, 'jobs', id)))
}

export async function adminGetProviders(): Promise<Provider[]> {
  const snap = await getDocs(collection(db, 'providers'))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Provider))
    .sort((a, b) => a.name.localeCompare(b.name))
}

export async function adminCreateProvider(data: AdminProviderInput): Promise<string> {
  return logged('adminCreateProvider', async () => {
    const ref = await addDoc(collection(db, 'providers'), { ...data, createdAt: Date.now(), updatedAt: Date.now() })
    return ref.id
  })
}

export async function adminUpdateProvider(id: string, data: Partial<AdminProviderInput>): Promise<void> {
  await logged('adminUpdateProvider', () => updateDoc(doc(db, 'providers', id), { ...data, updatedAt: Date.now() }))
}

export async function adminDeleteProvider(id: string): Promise<void> {
  await logged('adminDeleteProvider', () => deleteDoc(doc(db, 'providers', id)))
}

export async function adminGetGovPrograms(): Promise<GovProgram[]> {
  const snap = await getDocs(collection(db, 'gov_aid_programs'))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as GovProgram))
    .sort((a, b) => a.name.localeCompare(b.name))
}

export async function adminCreateGovProgram(data: AdminGovProgramInput): Promise<string> {
  return logged('adminCreateGovProgram', async () => {
    const ref = await addDoc(collection(db, 'gov_aid_programs'), { ...data, createdAt: Date.now(), updatedAt: Date.now() })
    return ref.id
  })
}

export async function adminUpdateGovProgram(id: string, data: Partial<AdminGovProgramInput>): Promise<void> {
  await logged('adminUpdateGovProgram', () => updateDoc(doc(db, 'gov_aid_programs', id), { ...data, updatedAt: Date.now() }))
}

export async function adminDeleteGovProgram(id: string): Promise<void> {
  await logged('adminDeleteGovProgram', () => deleteDoc(doc(db, 'gov_aid_programs', id)))
}

export async function adminGetNgoPrograms(): Promise<NgoProgram[]> {
  const snap = await getDocs(collection(db, 'ngo_programs'))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as NgoProgram))
    .sort((a, b) => a.name.localeCompare(b.name))
}

export async function adminCreateNgoProgram(data: AdminNgoProgramInput): Promise<string> {
  return logged('adminCreateNgoProgram', async () => {
    const ref = await addDoc(collection(db, 'ngo_programs'), { ...data, createdAt: Date.now(), updatedAt: Date.now() })
    return ref.id
  })
}

export async function adminUpdateNgoProgram(id: string, data: Partial<AdminNgoProgramInput>): Promise<void> {
  await logged('adminUpdateNgoProgram', () => updateDoc(doc(db, 'ngo_programs', id), { ...data, updatedAt: Date.now() }))
}

export async function adminDeleteNgoProgram(id: string): Promise<void> {
  await logged('adminDeleteNgoProgram', () => deleteDoc(doc(db, 'ngo_programs', id)))
}
